/**
 * Retry with Exponential Backoff
 * Network resilience for OAuth token exchange and profile fetch
 */

export interface RetryConfig {
  maxRetries: number;
  initialDelayMs: number;
  maxDelayMs: number;
  backoffMultiplier: number;
  retryableStatusCodes: number[];
}

export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries:           3,
  initialDelayMs:       800,
  maxDelayMs:           5000,
  backoffMultiplier:    2,
  retryableStatusCodes: [429, 500, 502, 503, 504],
};

/**
 * Check if an error is retryable
 */
export function isRetryableError(error: unknown, config: RetryConfig = DEFAULT_RETRY_CONFIG): boolean {
  // Don't retry these OAuth-specific errors
  const nonRetryablePatterns = [
    'invalid_grant',
    'invalid_code',
    'state_mismatch',
    'unauthorized_client',
    'invalid_request',
    'access_denied',
    'unsupported_response_type',
    'redirect_uri_mismatch',
  ];

  if (error instanceof Error) {
    const message = error.message.toLowerCase();

    // Check for non-retryable patterns
    for (const pattern of nonRetryablePatterns) {
      if (message.includes(pattern)) {
        return false;
      }
    }

    // Check for retryable patterns
    if (
      message.includes('network')    ||
      message.includes('timeout')    ||
      message.includes('fetch')      ||
      message.includes('econnreset') ||
      message.includes('enotfound')
    ) {
      return true;
    }

    // Check for retryable status codes
    for (const code of config.retryableStatusCodes) {
      if (message.includes(String(code))) {
        return true;
      }
    }
  }

  // Handle fetch Response errors
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status: number }).status;
    return config.retryableStatusCodes.includes(status);
  }

  return false;
}

/**
 * Sleep for a specified duration
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Execute a function with exponential backoff retry
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  config: Partial<RetryConfig> = {},
  onRetry?: (attempt: number, error: unknown, delayMs: number) => void
): Promise<T> {
  const fullConfig: RetryConfig = { ...DEFAULT_RETRY_CONFIG, ...config };

  let lastError: unknown;
  let delay = fullConfig.initialDelayMs;

  for (let attempt = 0; attempt <= fullConfig.maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Check if we should retry
      if (attempt >= fullConfig.maxRetries || !isRetryableError(error, fullConfig)) {
        throw error;
      }

      // FIX: Original multiplied `delay` BEFORE sleeping, so `initialDelayMs` (800ms)
      // was never used as the first retry delay — the first retry always waited
      // `initialDelayMs * backoffMultiplier` (1600ms).
      //
      // Fix: sleep the current delay first, then multiply for the next iteration.
      // Sequence with initialDelayMs=800, backoffMultiplier=2:
      //   Retry 1: sleep 800ms  → delay becomes 1600
      //   Retry 2: sleep 1600ms → delay becomes 3200
      //   Retry 3: sleep 3200ms → delay becomes 5000 (capped)
      console.log(`[RetryWithBackoff] Attempt ${attempt + 1} failed, retrying in ${delay}ms...`);
      onRetry?.(attempt + 1, error, delay);

      await sleep(delay);
      delay = Math.min(delay * fullConfig.backoffMultiplier, fullConfig.maxDelayMs);
    }
  }

  throw lastError;
}

/**
 * Check if the device is online
 */
export function checkOnline(): boolean {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine;
}

/**
 * Wait for the device to come online (with timeout)
 */
export function waitForOnline(timeoutMs: number = 10000): Promise<boolean> {
  return new Promise(resolve => {
    if (checkOnline()) {
      resolve(true);
      return;
    }

    const timeout = setTimeout(() => {
      window.removeEventListener('online', onOnline);
      resolve(false);
    }, timeoutMs);

    const onOnline = () => {
      clearTimeout(timeout);
      resolve(true);
    };

    window.addEventListener('online', onOnline, { once: true });
  });
}
